import React, { useState, useEffect } from 'react';
import {
  Brain, Calculator, RefreshCw, Settings, ChevronDown, ChevronUp, Search,
  ClipboardCheck, History, AlertTriangle, CheckCircle, ShieldCheck,
} from 'lucide-react';
import {
  useGetParametresRachatQuery,
  useAnalyserRachatMutation,
  useProposerRachatsMutation,
} from '../../services/rachatApi';
import type { EtudiantRachat, AnalyseUE } from '../../services/rachatApi';
import ValidationDemandes from './ValidationDemandes';
import HistoriqueRachats from './HistoriqueRachats';
import VueCommission from './VueCommission';

// ─── Tabs ────────────────────────────────────────────────────────────────────

type TabKey = 'analyse' | 'validation' | 'historique' | 'commission';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'analyse', label: 'Analyse', icon: <Brain size={16} /> },
  { key: 'validation', label: 'Validation & Demandes', icon: <ClipboardCheck size={16} /> },
  { key: 'historique', label: 'Historique', icon: <History size={16} /> },
  { key: 'commission', label: 'Vue Commission', icon: <ShieldCheck size={16} /> },
];

// ─── UE Card (expandable) ────────────────────────────────────────────────────

const UECard: React.FC<{ ue: AnalyseUE }> = ({ ue }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700/30"
      >
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-gray-700 dark:text-gray-300">{ue.code_ue}</span>
          <span className="text-gray-600 dark:text-gray-400 truncate max-w-[200px]">{ue.nom_ue}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`font-mono font-semibold ${
            ue.moyenne_ue !== null && ue.moyenne_ue >= 10
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            {ue.moyenne_ue !== null ? ue.moyenne_ue.toFixed(2) : '—'}
          </span>
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${
            ue.validee
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
          }`}>
            {ue.validee ? 'Validée' : 'Non validée'}
          </span>
          <span className="text-gray-400 text-[10px]">{ue.credits_obtenus}/{ue.credits} cr.</span>
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </div>
      </button>

      {open && (
        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/20 border-t border-gray-200 dark:border-gray-600">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-gray-500 dark:text-gray-400">
                <th className="text-left py-1 font-medium">Matière</th>
                <th className="text-center py-1 font-medium">Moy. Devoir</th>
                <th className="text-center py-1 font-medium">Note Exam</th>
                <th className="text-center py-1 font-medium">Moy. ECUE</th>
                <th className="text-center py-1 font-medium">Rattrapage</th>
                <th className="text-center py-1 font-medium">Modifiable</th>
              </tr>
            </thead>
            <tbody>
              {ue.ecues.map(ecue => (
                <tr key={ecue.matiere_id} className="border-t border-gray-100 dark:border-gray-600/50">
                  <td className="py-1.5">
                    <span className="font-mono text-gray-500 mr-1">{ecue.code_matiere}</span>
                    <span className="text-gray-700 dark:text-gray-300">{ecue.nom_matiere}</span>
                  </td>
                  <td className="text-center font-mono text-gray-600 dark:text-gray-400">
                    {ecue.note_devoir !== null ? ecue.note_devoir.toFixed(2) : '—'}
                  </td>
                  <td className="text-center">
                    <span className={`font-mono font-semibold ${
                      ecue.absent ? 'text-gray-400' :
                      ecue.note_examen_actuelle !== null && ecue.note_examen_actuelle < 10
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-800 dark:text-gray-200'
                    }`}>
                      {ecue.absent ? 'ABS' : ecue.note_examen_actuelle !== null ? ecue.note_examen_actuelle.toFixed(2) : '—'}
                    </span>
                  </td>
                  <td className="text-center font-mono text-gray-700 dark:text-gray-300">
                    {ecue.moyenne_ecue !== null ? ecue.moyenne_ecue.toFixed(2) : '—'}
                  </td>
                  <td className="text-center">
                    {ecue.est_rattrapage ? (
                      <span className="text-orange-600 dark:text-orange-400 font-semibold">Oui</span>
                    ) : (
                      <span className="text-gray-400">Non</span>
                    )}
                  </td>
                  <td className="text-center">
                    {ecue.modifiable ? (
                      <span className="text-green-600 dark:text-green-400">Oui</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Analyse Tab ─────────────────────────────────────────────────────────────

const AnalyseTab: React.FC = () => {
  const [etudiants, setEtudiants] = useState<EtudiantRachat[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showParametres, setShowParametres] = useState(false);
  const [niveauFilter, setNiveauFilter] = useState('');
  const [semestreFilter, setSemestreFilter] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [erreur, setErreur] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [hasAnalysed, setHasAnalysed] = useState(false);

  const { data: parametresData } = useGetParametresRachatQuery();
  const [analyserRachat, { isLoading: analysant }] = useAnalyserRachatMutation();
  const [proposerRachats, { isLoading: proposant }] = useProposerRachatsMutation();

  const lancerAnalyse = async () => {
    setErreur('');
    setSuccessMsg('');
    try {
      const payload: { niveau?: string; semestre?: number } = {};
      if (niveauFilter) payload.niveau = niveauFilter;
      payload.semestre = semestreFilter;
      const result = await analyserRachat(payload).unwrap();
      setEtudiants(result.etudiants ?? []);
      setHasAnalysed(true);
      setSelectedIds([]);
    } catch (e: any) {
      setErreur(
        e?.data?.detail || e?.data?.error || e?.message ||
        "Erreur lors de l'analyse. Vérifiez que les résultats ont été calculés."
      );
      setEtudiants([]);
    }
  };

  useEffect(() => { lancerAnalyse(); }, []);

  const appliquerRachat = async () => {
    if (selectedIds.length === 0) return;
    setErreur('');
    setSuccessMsg('');
    try {
      const result = await proposerRachats({
        etudiants: selectedIds,
        semestre: semestreFilter,
        niveau: niveauFilter || undefined,
      }).unwrap();
      setSuccessMsg(`${result.demandes_creees} demande(s) de rachat créée(s). Allez dans l'onglet Validation pour simuler et approuver.`);
      setSelectedIds([]);
    } catch (e: any) {
      setErreur(e?.data?.error || "Erreur lors de la création des demandes.");
    }
  };

  const toggleSelection = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllEligibles = () => {
    setSelectedIds(etudiants.filter(e => e.eligible).map(e => e.id));
  };

  const filtered = searchQuery
    ? etudiants.filter(e =>
        e.matricule.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (e.prenoms && e.prenoms.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : etudiants;

  const stats = {
    total: etudiants.length,
    eligibles: etudiants.filter(e => e.eligible).length,
    nonEligibles: etudiants.filter(e => !e.eligible).length,
  };

  return (
    <div className="space-y-5">
      {/* Messages */}
      {erreur && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
          {erreur}
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-700 dark:text-green-300 text-sm">
          {successMsg}
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau</label>
            <select
              value={niveauFilter}
              onChange={e => setNiveauFilter(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous les niveaux</option>
              {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre</label>
            <select
              value={semestreFilter}
              onChange={e => setSemestreFilter(parseInt(e.target.value))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value={1}>Semestre 1</option>
              <option value={2}>Semestre 2</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rechercher</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Matricule, nom..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={lancerAnalyse}
              disabled={analysant}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              <RefreshCw size={14} className={analysant ? 'animate-spin' : ''} />
              {analysant ? 'Analyse...' : 'Analyser'}
            </button>
            <button
              onClick={() => setShowParametres(!showParametres)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
              title="Paramètres"
            >
              <Settings size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Paramètres */}
      {showParametres && parametresData && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Paramètres du Rachat</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Seuil admission', val: parametresData.seuil_admission },
              { label: 'Seuil élim. UE', val: parametresData.seuil_eliminatoire_ue },
              { label: 'Seuil min. rachat', val: parametresData.seuil_minimum_rachat },
              { label: 'Plafond augmentation', val: parametresData.plafond_augmentation },
              { label: 'Note éliminatoire', val: parametresData.note_eliminatoire },
            ].map(p => (
              <div key={p.label}>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{p.label}</label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {p.val}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total analysés', value: stats.total, icon: <Brain size={20} />, color: 'blue' },
          { label: 'Éligibles au rachat', value: stats.eligibles, icon: <CheckCircle size={20} />, color: 'green' },
          { label: 'Non éligibles', value: stats.nonEligibles, icon: <AlertTriangle size={20} />, color: 'red' },
        ].map(card => (
          <div key={card.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">{card.label}</span>
              <span className={`text-${card.color}-500`}>{card.icon}</span>
            </div>
            <div className={`text-2xl font-bold text-${card.color}-600 dark:text-${card.color}-400`}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Actions de Rachat</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {selectedIds.length} étudiant(s) sélectionné(s)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={selectAllEligibles}
            disabled={stats.eligibles === 0}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40"
          >
            Sélectionner les éligibles
          </button>
          <button
            onClick={() => setSelectedIds([])}
            disabled={selectedIds.length === 0}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40"
          >
            Désélectionner
          </button>
          <button
            onClick={appliquerRachat}
            disabled={selectedIds.length === 0 || proposant}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
          >
            <Calculator size={16} />
            {proposant ? 'Création...' : 'Proposer Rachat Auto'}
          </button>
        </div>
      </div>

      {/* Liste étudiants UE-centrique */}
      <div className="space-y-3">
        {analysant ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center py-16">
            <RefreshCw size={24} className="animate-spin text-blue-500 mr-3" />
            <span className="text-gray-500 dark:text-gray-400">Analyse en cours...</span>
          </div>
        ) : filtered.length > 0 ? (
          filtered.map(etd => {
            const isExpanded = expandedId === etd.id;
            return (
              <div
                key={etd.id}
                className={`bg-white dark:bg-gray-800 rounded-lg border overflow-hidden ${
                  etd.eligible
                    ? 'border-green-200 dark:border-green-800'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                {/* Student header */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(etd.id)}
                    onChange={() => toggleSelection(etd.id)}
                    disabled={!etd.eligible}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 disabled:opacity-30"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {etd.nom} {etd.prenoms}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {etd.matricule} — {etd.classe || etd.niveau}
                        {etd.option ? ` — ${etd.option}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {etd.raisons.map((r, i) => (
                        <span key={i} className="text-xs text-gray-500 dark:text-gray-400">{r}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {!etd.rattrapage_saisie && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 font-semibold">
                        Pas de rattrapage
                      </span>
                    )}
                    <div className="text-center">
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">Moy. Sem.</div>
                      <div className={`text-lg font-bold ${
                        etd.moy_semestre_actuelle >= 10
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {etd.moy_semestre_actuelle.toFixed(2)}
                      </div>
                    </div>
                    {etd.deficit > 0 && (
                      <div className="text-center">
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">Déficit</div>
                        <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                          -{etd.deficit.toFixed(2)}
                        </div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">Crédits</div>
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {etd.credits_obtenus}/{etd.credits_requis}
                      </div>
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                      etd.eligible
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {etd.eligible ? 'Éligible' : 'Non éligible'}
                    </span>
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : etd.id)}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400"
                    >
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Expanded: UE cards */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                    <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                      Unités d'Enseignement ({etd.ues.length})
                    </h4>
                    {etd.ues.map(ue => (
                      <UECard key={ue.code_ue} ue={ue} />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        ) : hasAnalysed ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 py-16 text-center">
            <Brain size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">Aucun étudiant trouvé.</p>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
              Assurez-vous que les résultats (ECUE, UE, Semestre) ont été calculés.
            </p>
          </div>
        ) : null}
      </div>

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
        <div className="flex items-start gap-3">
          <Brain size={18} className="text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-200 text-sm mb-1">Fonctionnement du Rachat</h4>
            <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-0.5">
              <li>1. Saisir les notes de rattrapage, puis calculer les résultats (ECUE, UE, Semestre)</li>
              <li>2. Analyser ici pour identifier les étudiants éligibles (moy &lt; 10, pas d'UE éliminatoire)</li>
              <li>3. "Proposer Rachat Auto" calcule les augmentations minimales des notes d'examen</li>
              <li>4. Onglet "Validation" : le jury simule, ajuste manuellement les notes, puis approuve</li>
              <li>5. L'approbation modifie les notes d'examen et recalcule ECUE → UE → Semestre</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Composant Principal ────────────────────────────────────────────────────

export const RachatIntelligent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('analyse');

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Rachat Intelligent</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Analyse UE-centrique, simulation et validation des rachats — basé sur les notes d'examen
        </p>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-0 -mb-px">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'analyse' && <AnalyseTab />}
      {activeTab === 'validation' && <ValidationDemandes />}
      {activeTab === 'historique' && <HistoriqueRachats />}
      {activeTab === 'commission' && <VueCommission />}
    </div>
  );
};

export default RachatIntelligent;
